import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { downloadBlob } from '../../../helper/table-utils';
import { Httpservice } from '../../../services/httpservice';
import { SscShellComponent } from '../ssc-shell/ssc-shell';

interface BatchLanguage {
  language_code: string;
  language_name: string;
  native_name: string | null;
}

interface BatchSubject {
  subject_code: string;
  subject_name: string;
}

interface BatchFileConfig {
  id: string;
  file: File;
  name: string;
  sourceLanguage: string;
  subject: string;
  targetLanguages: string[];
  targetLanguageSearch: string;
  targetLanguagePanelOpen: boolean;
  estimatedReadyAt: string;
  estimatedSeconds: number;
}

interface BatchFileStatus {
  id: string;
  name: string;
  done: number;
  total: number;
  completedBatches: number;
  totalBatches: number;
  currentBatchLabel: string;
  status: 'Queued' | 'Processing' | 'Ready' | 'Failed';
}

interface BatchProgressFile {
  id: string;
  file_name: string;
  status: string;
  done_questions: number;
  total_questions: number;
  completed_batches: number;
  total_batches: number;
  current_batch?: { label?: string } | null;
}

interface BatchProgressValue {
  status?: string;
  files?: BatchProgressFile[];
  results?: BatchFileResult[];
}

interface BatchTranslationDetail {
  target_language: string;
  translated_text: string;
  confidence: number | string | null;
  adequacy: number | string | null;
  fluency: number | string | null;
  terminology: string | null;
  formatting: string | null;
  notes: string | null;
  status: string;
}

interface BatchQuestionResult {
  row_number: number;
  source_language?: string;
  target_languages?: string[];
  subject: string;
  question: string;
  extracted_text?: string;
  file_path?: string;
  translations: BatchTranslationDetail[];
  status: string;
}

interface BatchFileResult {
  id?: string;
  file_name: string;
  download_file_name: string;
  content_base64: string;
  total_questions: number;
  done_questions: number;
  status: string;
  result_type?: string;
  questions: BatchQuestionResult[];
}

@Component({
  selector: 'app-batch-integration-page',
  standalone: true,
  imports: [CommonModule, FormsModule, SscShellComponent],
  templateUrl: './batch-integration.html',
  styleUrl: './batch-integration.scss',
})
export class BatchIntegrationPage implements OnInit {
  protected languages: BatchLanguage[] = [];
  protected subjects: BatchSubject[] = [];
  protected pendingConfigs: BatchFileConfig[] = [];
  protected fileConfigs: BatchFileConfig[] = [];
  protected fileStatuses: BatchFileStatus[] = [];
  protected batchResults: BatchFileResult[] = [];
  protected expandedFileName = '';
  protected selectedQuestion: { file: BatchFileResult; question: BatchQuestionResult } | null = null;
  protected selectedTranslationTarget = '';
  protected reviewTranslationText = '';
  protected reviewSaving = false;
  protected estimatedReadyAt = '-';
  protected isProcessing = false;
  protected errorMessage = '';

  private pollingTimer: number | null = null;
  private activeJobId = '';
  private readonly defaultQuestionCount = 25;

  constructor(private readonly httpSvc: Httpservice, private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.loadLanguages();
    this.loadSubjects();
    this.updateTotalEstimate();
  }

  protected get selectedFilesCount(): number {
    return this.fileConfigs.length;
  }

  protected get finalizedFilesLabel(): string {
    return this.fileConfigs.some((config) => this.isImageFile(config.file)) ? 'Finalized Files' : 'Finalized Excels';
  }

  protected get pendingFilesCount(): number {
    return this.pendingConfigs.length;
  }

  protected get targetLanguageCount(): number {
    return this.fileConfigs.reduce((total, config) => total + config.targetLanguages.length, 0);
  }

  protected get canRunBatch(): boolean {
    return !!this.runnableFileConfigs().length && !this.pendingConfigs.length && !this.isProcessing;
  }

  protected onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!this.canAddFiles(files)) {
      this.errorMessage = 'Upload either Excel files or image files at one time, not both.';
      input.value = '';
      return;
    }
    const nextConfigs = files.map((file) => this.createFileConfig(file));
    this.pendingConfigs = [...this.pendingConfigs, ...nextConfigs];
    this.errorMessage = '';
    input.value = '';
  }

  protected markConfigDirty(config: BatchFileConfig): void {
    this.refreshConfigEstimate(config);
  }

  protected finalizeFileConfig(config: BatchFileConfig): void {
    if (!this.isConfigReady(config)) {
      this.errorMessage = 'Choose at least one target language before finalizing image files.';
      return;
    }

    this.pendingConfigs = this.pendingConfigs.filter((item) => item.id !== config.id);
    this.fileConfigs = [...this.fileConfigs, config];
    this.fileStatuses = [...this.fileStatuses, this.buildQueuedStatus(config)];
    this.errorMessage = '';
    this.refreshConfigEstimate(config);
    this.updateTotalEstimate();
  }

  protected removePendingFile(config: BatchFileConfig): void {
    this.pendingConfigs = this.pendingConfigs.filter((item) => item.id !== config.id);
  }

  protected removeFinalizedFile(config: BatchFileConfig): void {
    if (this.isProcessing) {
      return;
    }
    this.fileConfigs = this.fileConfigs.filter((item) => item.id !== config.id);
    this.fileStatuses = this.fileStatuses.filter((item) => item.id !== config.id);
    this.batchResults = this.batchResults.filter((item) => item.id !== config.id);
    if (this.selectedQuestion?.file?.id === config.id) {
      this.selectedQuestion = null;
    }
    this.updateTotalEstimate();
  }

  protected editFinalizedFile(config: BatchFileConfig): void {
    if (this.isProcessing) {
      return;
    }

    this.fileConfigs = this.fileConfigs.filter((item) => item.id !== config.id);
    this.pendingConfigs = [config, ...this.pendingConfigs.filter((item) => item.id !== config.id)];
    this.fileStatuses = this.fileStatuses.filter((item) => item.id !== config.id);
    this.batchResults = this.batchResults.filter((item) => item.id !== config.id);
    this.errorMessage = '';
    if (this.selectedQuestion?.file?.id === config.id) {
      this.selectedQuestion = null;
    }
    this.updateTotalEstimate();
  }

  protected isConfigReady(config: BatchFileConfig): boolean {
    return !this.isImageFile(config.file) || !!config.targetLanguages.length;
  }

  protected filteredTargetLanguages(config: BatchFileConfig): BatchLanguage[] {
    const searchTerm = config.targetLanguageSearch.trim().toLowerCase();
    const availableLanguages = this.languages.filter((language) => !config.targetLanguages.includes(language.language_name));
    if (!searchTerm) {
      return availableLanguages;
    }
    return availableLanguages.filter((language) => this.languageLabel(language).toLowerCase().includes(searchTerm));
  }

  protected addTargetLanguage(config: BatchFileConfig, language: BatchLanguage): void {
    config.targetLanguages = [...config.targetLanguages, language.language_name];
    config.targetLanguageSearch = '';
    config.targetLanguagePanelOpen = false;
    this.refreshConfigEstimate(config);
  }

  protected removeTargetLanguage(config: BatchFileConfig, languageName: string): void {
    config.targetLanguages = config.targetLanguages.filter((item) => item !== languageName);
    this.refreshConfigEstimate(config);
  }

  protected closeTargetLanguagePanel(config: BatchFileConfig): void {
    window.setTimeout(() => {
      config.targetLanguagePanelOpen = false;
    }, 160);
  }

  protected downloadTemplate(): void {
    this.httpSvc
      .downloadfile(AuthEndPoints.DOWNLOAD_BATCH_TEMPLATE, ApiMethod.GET)
      .subscribe((blob) => downloadBlob(blob, 'ssc_translation_batch_template.xlsx'));
  }

  protected runBatch(): void {
    const runnableConfigs = this.runnableFileConfigs();
    if (!this.canRunBatch) {
      if (this.pendingConfigs.length) {
        this.errorMessage = 'Finalize selected Excel files before running the batch.';
      } else if (!runnableConfigs.length && this.fileConfigs.length) {
        this.errorMessage = 'All finalized Excel files are already translated.';
      }
      return;
    }

    this.errorMessage = '';
    this.selectedQuestion = null;
    this.selectedTranslationTarget = '';
    this.expandedFileName = '';
    this.isProcessing = true;
    this.fileStatuses = [
      ...this.fileStatuses.filter((status) => !runnableConfigs.some((config) => config.id === status.id)),
      ...runnableConfigs.map((config) => this.buildQueuedStatus(config)),
    ];
    this.httpSvc.requestCall(AuthEndPoints.RUN_BATCH_TRANSLATION, ApiMethod.POST, this.buildRunFormData(runnableConfigs)).subscribe({
      next: (response) => {
        if (response?.status === 'FALSE') {
          this.errorMessage = response?.msg || 'Unable to queue batch files.';
          this.isProcessing = false;
          this.cdr.markForCheck();
          return;
        }
        const value = this.isRecord(response?.value) ? response.value : {};
        this.activeJobId = String(value['job_id'] ?? '');
        this.applyProgress(value['progress'] ?? value);
        if (this.activeJobId) {
          this.startPollingJob();
        } else {
          this.errorMessage = 'Batch job was not created.';
          this.isProcessing = false;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Unable to queue batch files.';
        this.isProcessing = false;
      },
    });
  }

  protected viewFileDetails(file: BatchFileResult): void {
    this.expandedFileName = file.file_name;
    this.selectedQuestion = null;
    this.selectedTranslationTarget = '';
  }

  protected hideFileDetails(file: BatchFileResult): void {
    if (this.expandedFileName === file.file_name) {
      this.expandedFileName = '';
      if (this.selectedQuestion?.file?.file_name === file.file_name) {
        this.selectedQuestion = null;
        this.selectedTranslationTarget = '';
      }
    }
  }

  protected selectQuestion(file: BatchFileResult, question: BatchQuestionResult): void {
    this.selectedQuestion = { file, question };
    this.selectedTranslationTarget = question.translations[0]?.target_language ?? '';
    this.syncReviewTranslationText();
  }

  protected closeQuestionDetails(): void {
    this.selectedQuestion = null;
    this.selectedTranslationTarget = '';
  }

  protected selectTranslationTarget(targetLanguage: string): void {
    this.selectedTranslationTarget = targetLanguage;
    this.syncReviewTranslationText();
  }

  protected activeTranslation(question: BatchQuestionResult): BatchTranslationDetail | null {
    return question.translations.find((translation) => translation.target_language === this.selectedTranslationTarget)
      ?? question.translations[0]
      ?? null;
  }

  protected detailTargetLanguages(question: BatchQuestionResult): string {
    const rowTargets = Array.isArray(question.target_languages) ? question.target_languages.filter(Boolean) : [];
    if (rowTargets.length) {
      return rowTargets.join(', ');
    }
    const translatedTargets = question.translations.map((item) => item.target_language).filter(Boolean);
    return translatedTargets.length ? translatedTargets.join(', ') : '-';
  }

  protected downloadResultWorkbook(file: BatchFileResult): void {
    const bytes = this.base64ToBytes(file.content_base64);
    const arrayBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    const isTextResult = file.result_type === 'image' || file.download_file_name?.endsWith('.txt');
    const blob = new Blob([arrayBuffer], {
      type: isTextResult ? 'text/plain;charset=utf-8' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadBlob(blob, file.download_file_name || (isTextResult ? 'translated_images.txt' : 'translated_batch.xlsx'));
  }

  protected questionPreview(question: string): string {
    return question.length > 120 ? `${question.slice(0, 120)}...` : question;
  }

  protected questionDisplayText(question: BatchQuestionResult): string {
    return question.question || question.extracted_text || '[Image question]';
  }

  protected translationDisplayText(translation: BatchTranslationDetail): string {
    return this.removeDuplicatedTrailingOptions(translation.translated_text || '');
  }

  protected reviewStatusClass(status: string): string {
    return `review-${String(status || 'Draft').toLowerCase().replace(/\s+/g, '-')}`;
  }

  protected updateSelectedTranslationReview(status: 'Draft' | 'Approved' | 'Rejected' | 'Needs Review'): void {
    if (!this.selectedQuestion || this.reviewSaving) {
      return;
    }
    const translation = this.activeTranslation(this.selectedQuestion.question);
    if (!translation) {
      return;
    }
    this.reviewSaving = true;
    this.httpSvc.requestCall(AuthEndPoints.REVIEW_BATCH_TRANSLATION, ApiMethod.POST, {
      excel_name: this.selectedQuestion.file.file_name,
      row_number: this.selectedQuestion.question.row_number,
      target_language: translation.target_language,
      translation: this.reviewTranslationText,
      status,
    }, false, { suppressToast: true }).subscribe({
      next: (response) => {
        if (response?.status === 'FALSE') {
          this.errorMessage = response?.msg || 'Unable to update review status.';
        } else {
          translation.translated_text = this.reviewTranslationText;
          translation.status = status;
          this.errorMessage = '';
        }
        this.reviewSaving = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Unable to update review status.';
        this.reviewSaving = false;
        this.cdr.markForCheck();
      },
    });
  }

  protected approveAllTranslations(file: BatchFileResult): void {
    if (this.reviewSaving) {
      return;
    }
    this.reviewSaving = true;
    this.httpSvc.requestCall(AuthEndPoints.REVIEW_BATCH_FILE, ApiMethod.POST, {
      excel_name: file.file_name,
      status: 'Approved',
    }, false, { suppressToast: true }).subscribe({
      next: (response) => {
        if (response?.status === 'FALSE') {
          this.errorMessage = response?.msg || 'Unable to approve file translations.';
        } else {
          file.questions.forEach((question) => question.translations.forEach((translation) => {
            translation.status = 'Approved';
          }));
          this.errorMessage = '';
        }
        this.reviewSaving = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Unable to approve file translations.';
        this.reviewSaving = false;
        this.cdr.markForCheck();
      },
    });
  }

  protected excelNamePreview(fileName: string): string {
    if (fileName.length <= 34) {
      return fileName;
    }
    const extensionIndex = fileName.lastIndexOf('.');
    const extension = extensionIndex > 0 ? fileName.slice(extensionIndex) : '';
    const baseName = extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;
    const endingLength = Math.min(12, Math.max(8, baseName.length - 16));
    return `${baseName.slice(0, 16)}.....${baseName.slice(-endingLength)}${extension}`;
  }

  protected progressPercent(file: BatchFileStatus): number {
    if (file.status === 'Ready') {
      return 100;
    }
    return file.total ? Math.min((file.done / file.total) * 100, 100) : 0;
  }

  protected resultForConfig(config: BatchFileConfig): BatchFileResult | undefined {
    return this.batchResults.find((file) => file.id === config.id || file.file_name === config.name);
  }

  private createFileConfig(file: File): BatchFileConfig {
    const config = {
      id: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      file,
      name: file.name,
      sourceLanguage: '',
      subject: '',
      targetLanguages: [],
      targetLanguageSearch: '',
      targetLanguagePanelOpen: false,
      estimatedReadyAt: '-',
      estimatedSeconds: 0,
    };
    this.refreshConfigEstimate(config);
    return config;
  }

  private syncReviewTranslationText(): void {
    const translation = this.selectedQuestion ? this.activeTranslation(this.selectedQuestion.question) : null;
    this.reviewTranslationText = translation ? this.translationDisplayText(translation) : '';
  }

  private removeDuplicatedTrailingOptions(value: string): string {
    const parts = value.split(/\n{2,}/);
    if (parts.length < 2) {
      return value;
    }
    const tail = parts[parts.length - 1].trim();
    const beforeTail = parts.slice(0, -1).join('\n\n');
    const tailLines = tail.split('\n').map((line) => line.trim()).filter(Boolean);
    if (!tailLines.length) {
      return beforeTail;
    }
    const normalizedBefore = this.normalizeComparisonText(beforeTail);
    const tailIsDuplicate = tailLines.every((line) => {
      const normalizedLine = this.normalizeComparisonText(line);
      return normalizedLine.length >= 3 && normalizedBefore.includes(normalizedLine);
    });
    return tailIsDuplicate ? beforeTail : value;
  }

  private normalizeComparisonText(value: string): string {
    return value
      .toLowerCase()
      .replace(/[✓✔]/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, '');
  }

  private canAddFiles(files: File[]): boolean {
    if (!files.length) {
      return true;
    }
    const nextType = this.isImageFile(files[0]) ? 'image' : 'excel';
    if (!files.every((file) => (this.isImageFile(file) ? 'image' : 'excel') === nextType)) {
      return false;
    }
    const existingFiles = [...this.pendingConfigs, ...this.fileConfigs];
    if (!existingFiles.length) {
      return true;
    }
    const existingType = this.isImageFile(existingFiles[0].file) ? 'image' : 'excel';
    return existingType === nextType;
  }

  private isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  private buildQueuedStatus(config: BatchFileConfig): BatchFileStatus {
    return {
      id: config.id,
      name: config.name,
      done: 0,
      total: 0,
      completedBatches: 0,
      totalBatches: 0,
      currentBatchLabel: '',
      status: 'Queued',
    };
  }

  private buildRunFormData(configs: BatchFileConfig[]): FormData {
    const formData = new FormData();
    configs.forEach((config) => formData.append('files', config.file));
    formData.append('file_configs', JSON.stringify(configs.map((config) => ({
      id: config.id,
      file_name: config.name,
      source_language: config.sourceLanguage,
      subject: config.subject,
      target_languages: config.targetLanguages,
    }))));
    return formData;
  }

  private runnableFileConfigs(): BatchFileConfig[] {
    return this.fileConfigs.filter((config) => !this.resultForConfig(config) && this.statusForConfig(config)?.status !== 'Ready');
  }

  private loadLanguages(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_INDIAN_LANGUAGES, ApiMethod.GET, '', false, { suppressToast: true })
      .subscribe({
        next: (response) => {
          const rows = this.extractArray(response, 'languages');
          this.languages = rows.map((row) => ({
            language_code: String(row['language_code'] ?? ''),
            language_name: String(row['language_name'] ?? ''),
            native_name: typeof row['native_name'] === 'string' ? row['native_name'] : null,
          }));
          [...this.pendingConfigs, ...this.fileConfigs].forEach((config) => {
            config.sourceLanguage = config.sourceLanguage || '';
          });
        },
      });
  }

  private loadSubjects(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_TRANSLATION_WORKSPACE_SUBJECTS, ApiMethod.GET, '', false, { suppressToast: true })
      .subscribe({
        next: (response) => {
          const rows = this.extractArray(response, 'subjects');
          this.subjects = rows.map((row) => ({
            subject_code: String(row['subject_code'] ?? ''),
            subject_name: String(row['subject_name'] ?? ''),
          }));
          [...this.pendingConfigs, ...this.fileConfigs].forEach((config) => {
            config.subject = config.subject || '';
          });
        },
      });
  }

  private refreshConfigEstimate(config: BatchFileConfig): void {
    const formData = new FormData();
    formData.append('file_count', '1');
    formData.append('question_count', String(this.defaultQuestionCount));
    formData.append('target_language_count', String(Math.max(config.targetLanguages.length, 1)));

    this.httpSvc.requestCall(AuthEndPoints.ESTIMATE_BATCH_TRANSLATION, ApiMethod.POST, formData, false, { suppressToast: true }).subscribe({
      next: (response) => {
        config.estimatedReadyAt = response?.value?.ready_at ?? '-';
        config.estimatedSeconds = Number(response?.value?.estimated_seconds ?? 0);
        this.updateTotalEstimate();
        this.syncQueuedStatusTotal(config);
        this.cdr.markForCheck();
      },
    });
  }

  private updateTotalEstimate(): void {
    if (!this.fileConfigs.length) {
      this.estimatedReadyAt = '-';
      return;
    }

    const totalSeconds = this.fileConfigs.reduce((total, config) => {
      const seconds = config.estimatedSeconds || this.estimatedWorkUnits(config) * 8;
      return total + seconds;
    }, 0);
    this.estimatedReadyAt = this.formatReadyAt(totalSeconds);
  }

  private startPollingJob(): void {
    this.stopPollingJob();
    this.pollingTimer = window.setInterval(() => this.pollJobStatus(), 1500);
    this.pollJobStatus();
  }

  private stopPollingJob(): void {
    if (this.pollingTimer !== null) {
      window.clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  private pollJobStatus(): void {
    if (!this.activeJobId) {
      return;
    }

    this.httpSvc
      .requestCall(`${AuthEndPoints.BATCH_TRANSLATION_STATUS}/${this.activeJobId}`, ApiMethod.GET, '', false, { suppressToast: true })
      .subscribe({
        next: (response) => {
          if (response?.status === 'FALSE') {
            this.errorMessage = response?.msg || 'Batch failed.';
          }
          if (this.applyCompletedStatusResponse(response?.value)) {
            this.cdr.markForCheck();
            return;
          }
          this.applyProgress(response?.value);
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage = 'Unable to fetch batch status.';
          this.stopPollingJob();
          this.isProcessing = false;
        },
      });
  }

  private applyProgress(progress: unknown): void {
    if (!this.isRecord(progress)) {
      return;
    }

    const progressFiles = Array.isArray(progress['files']) ? progress['files'].filter(this.isRecord) as unknown as BatchProgressFile[] : [];
    if (progressFiles.length) {
      const updatedStatuses = progressFiles.map((file) => ({
        id: String(file.id ?? ''),
        name: String(file.file_name ?? ''),
        done: Number(file.done_questions ?? 0),
        total: Number(file.total_questions ?? 0),
        completedBatches: Number(file.completed_batches ?? 0),
        totalBatches: Number(file.total_batches ?? 0),
        currentBatchLabel: file.current_batch?.label ? String(file.current_batch.label) : '',
        status: this.mapStatus(file.status),
      }));
      this.fileStatuses = [
        ...this.fileStatuses.filter((status) => !updatedStatuses.some((updated) => updated.id === status.id || updated.name === status.name)),
        ...updatedStatuses,
      ];
    }

    if (!progressFiles.length && String(progress['status'] ?? '').toLowerCase() === 'pending') {
      return;
    }

    const results = this.extractBatchResults({ value: progress });
    if (results.length) {
      this.mergeBatchResults(results);
      this.applyCompletedResultsToStatuses(results);
    }

    const status = String(progress['status'] ?? '').toLowerCase();
    const allFilesFinished = this.fileStatuses.length > 0 && this.fileStatuses.every((file) => file.status === 'Ready' || file.status === 'Failed');
    if (status === 'completed' || status === 'failed' || allFilesFinished) {
      this.stopPollingJob();
      this.isProcessing = false;
    }
  }

  private applyCompletedStatusResponse(value: unknown): boolean {
    if (!this.isRecord(value) || String(value['status'] ?? '').toLowerCase() !== 'completed') {
      return false;
    }

    const results = Array.isArray(value['results']) ? value['results'] as BatchFileResult[] : [];
    const progressFiles = Array.isArray(value['files']) ? value['files'] as BatchProgressFile[] : [];
    if (results.length) {
      this.mergeBatchResults(results);
      const nextStatuses = this.fileStatuses.filter((status) => !results.some((file) => file.id === status.id || file.file_name === status.name));
      const completedStatuses = results.map((result) => {
        const config = this.fileConfigs.find((item) => item.id === result.id || item.name === result.file_name);
        const progressFile = progressFiles.find((file) => file.id === result.id || file.file_name === result.file_name || (config && (file.id === config.id || file.file_name === config.name)));
        const previousStatus = config ? this.statusForConfig(config) : this.fileStatuses.find((status) => status.id === result.id || status.name === result.file_name);
        return {
          id: config?.id ?? result.id ?? '',
          name: config?.name ?? result.file_name,
          done: Number(progressFile?.done_questions ?? result.done_questions ?? result.total_questions ?? 0),
          total: Number(progressFile?.total_questions ?? result.total_questions ?? 0),
          completedBatches: Number(progressFile?.completed_batches ?? progressFile?.total_batches ?? previousStatus?.totalBatches ?? 0),
          totalBatches: Number(progressFile?.total_batches ?? previousStatus?.totalBatches ?? 0),
          currentBatchLabel: '',
          status: 'Ready' as const,
        };
      });
      this.fileStatuses = [...nextStatuses, ...completedStatuses];
    } else {
      this.applyProgress(value);
    }

    this.stopPollingJob();
    this.isProcessing = false;
    return true;
  }

  private statusForConfig(config: BatchFileConfig): BatchFileStatus | undefined {
    return this.fileStatuses.find((status) => status.id === config.id || status.name === config.name);
  }

  private mapStatus(value: string): BatchFileStatus['status'] {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'ready' || normalized === 'completed') {
      return 'Ready';
    }
    if (normalized === 'processing' || normalized === 'progress') {
      return 'Processing';
    }
    if (normalized === 'failed') {
      return 'Failed';
    }
    return 'Queued';
  }

  private syncQueuedStatusTotal(config: BatchFileConfig): void {
    this.fileStatuses = this.fileStatuses.map((item) => {
      if (item.id !== config.id || item.status !== 'Queued') {
        return item;
      }
      return item;
    });
  }

  private applyCompletedResultsToStatuses(results: BatchFileResult[]): void {
    this.fileStatuses = this.fileStatuses.map((status) => {
      const result = results.find((file) => file.id === status.id || file.file_name === status.name);
      if (!result) {
        return status;
      }
      return {
        ...status,
        done: Number(result.done_questions ?? result.total_questions ?? status.total),
        total: Number(result.total_questions ?? status.total),
        completedBatches: status.totalBatches || status.completedBatches ? status.totalBatches : status.completedBatches,
        currentBatchLabel: '',
        status: 'Ready',
      };
    });
  }

  private mergeBatchResults(results: BatchFileResult[]): void {
    const nextResults = [...this.batchResults];
    results.forEach((result) => {
      const index = nextResults.findIndex((file) => file.id === result.id || file.file_name === result.file_name);
      if (index >= 0) {
        nextResults[index] = result;
      } else {
        nextResults.push(result);
      }
    });
    this.batchResults = nextResults;
  }

  private estimatedWorkUnits(config: BatchFileConfig): number {
    return Math.max(this.defaultQuestionCount, 1);
  }

  private formatReadyAt(secondsFromNow: number): string {
    const readyAt = new Date(Date.now() + Math.max(secondsFromNow, 0) * 1000);
    return readyAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  }

  private extractArray(response: unknown, key: string): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.filter(this.isRecord);
    }
    if (!this.isRecord(response)) {
      return [];
    }
    const value = response['value'];
    if (this.isRecord(value) && Array.isArray(value[key])) {
      return value[key].filter(this.isRecord);
    }
    if (Array.isArray(response[key])) {
      return response[key].filter(this.isRecord);
    }
    return Array.isArray(value) ? value.filter(this.isRecord) : [];
  }

  private extractBatchResults(response: unknown): BatchFileResult[] {
    if (!this.isRecord(response)) {
      return [];
    }
    const value = response['value'];
    if (this.isRecord(value) && Array.isArray(value['results'])) {
      return value['results'] as BatchFileResult[];
    }
    if (this.isRecord(value) && Array.isArray(value['files'])) {
      const files = value['files'] as BatchProgressValue['files'];
      return (files ?? [])
        .filter((file) => this.isRecord(file) && typeof file['content_base64'] === 'string')
        .map((file) => file as unknown as BatchFileResult);
    }
    return [];
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private defaultSourceLanguage(): string {
    return this.languages.find((language) => language.language_name.toLowerCase() === 'english')?.language_name
      ?? this.languages[0]?.language_name
      ?? '';
  }

  private languageLabel(language: BatchLanguage): string {
    return `${language.language_name}${language.native_name ? ` - ${language.native_name}` : ''}`;
  }

  private base64ToBytes(value: string): Uint8Array {
    const binary = window.atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  }
}
